import React, { useState, useEffect, useMemo } from 'react';
import { Box, IconButton, useTheme, Button, Typography, TextField, MenuItem } from "@mui/material";
import { Delete, EditOutlined } from "@mui/icons-material";
import { tokens } from '../../theme';
import { useNavigate, useParams } from "react-router-dom";
import useDutiesWeeksService from '../../api/services/DutiesWeeksService';
import useTermService from '../../api/services/termService';
import Table from '../../components/Table';
import Header from "../../components/Header";
import LoadingSpinner from '../../components/LoadingSpinner';
import EditDialog from "./EditDutiesWeek";
import DeleteDialog from "./DeleteDutiesWeek";
import CreateDialog from "./CreateDutiesWeek";

const DutiesWeek = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const { termId } = useParams();
  const { dutiesWeeks, totalRecords, fetchDutiesWeeksByTerm, isLoading } = useDutiesWeeksService();
  const { fetchAllActiveTerms, terms } = useTermService();
  const [selectedTermId, setSelectedTermId] = useState(termId || '');
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedIdForEdit, setSelectedIdForEdit] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedDutiesWeekId, setSelectedDutiesWeekId] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  useEffect(() => {
    fetchAllActiveTerms(0, 100, 'ACTIVE');
  }, []);

  useEffect(() => {
    if (terms && terms.length > 0) {
      if (!selectedTermId || !terms.some(term => term.id === selectedTermId)) {
        const activeTerm = terms.find(term => term.status === 'ACTIVE');
        if (activeTerm) {
          setSelectedTermId(activeTerm.id);
        } else if (terms.length > 0) {
          setSelectedTermId(terms[0].id);
        }
      }
    }
  }, [terms, selectedTermId]);

  useEffect(() => {
    if (selectedTermId) {
      loadDutiesWeeks();
    }
  }, [selectedTermId]);

  const loadDutiesWeeks = async () => {
    if (selectedTermId) {
      await fetchDutiesWeeksByTerm(selectedTermId);
    }
  };

  const handleEditClick = (dutiesWeekId) => {
    setSelectedIdForEdit(dutiesWeekId);
    setIsEditDialogOpen(true);
  };

  const handleDeleteClick = (dutiesWeekId) => {
    setSelectedDutiesWeekId(dutiesWeekId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setSelectedDutiesWeekId(null);
    loadDutiesWeeks();
  };

  const openCreateDialog = () => {
    setIsDialogOpen(true);
  };

  const handleTermChange = (event) => {
    setSelectedTermId(event.target.value);
  };

  const selectedTerm = terms?.find(term => term.id === selectedTermId);

  // Sort duties weeks by start date (earliest first), then by end date
  const sortedDutiesWeeks = useMemo(() => {
    if (!dutiesWeeks || dutiesWeeks.length === 0) return dutiesWeeks;
    
    return [...dutiesWeeks].sort((a, b) => {
      // Handle null/undefined dates - put them at the end
      if (!a.weekStartDate && !b.weekStartDate) return 0;
      if (!a.weekStartDate) return 1;
      if (!b.weekStartDate) return -1;
      
      // Parse dates for comparison
      const dateA = new Date(a.weekStartDate);
      const dateB = new Date(b.weekStartDate);
      
      // Compare start dates
      if (dateA.getTime() !== dateB.getTime()) {
        return dateA.getTime() - dateB.getTime();
      }
      
      // If start dates are the same, compare end dates
      if (!a.weekEndDate && !b.weekEndDate) return 0;
      if (!a.weekEndDate) return 1;
      if (!b.weekEndDate) return -1;
      
      const endDateA = new Date(a.weekEndDate);
      const endDateB = new Date(b.weekEndDate);
      return endDateA.getTime() - endDateB.getTime();
    });
  }, [dutiesWeeks]);

  const columns = [
    { field: "weekName", headerName: "Week Name", flex: 1, minWidth: 150 },
    { 
      field: "weekStartDate", 
      headerName: "Start Date", 
      flex: 1,
      renderCell: (params) => {
        if (!params.value) return 'N/A';
        const date = new Date(params.value);
        return date.toLocaleString();
      }
    },
    { 
      field: "weekEndDate", 
      headerName: "End Date", 
      flex: 1,
      renderCell: (params) => {
        if (!params.value) return 'N/A';
        const date = new Date(params.value);
        return date.toLocaleString();
      }
    },
    { field: "termName", headerName: "Term", flex: 1 },
    { field: "status", headerName: "Status", flex: 1 },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      sortable: false,
      renderCell: ({ row }) => (
        <Box display="flex" justifyContent="center">
          <IconButton color="info" onClick={() => handleEditClick(row.id)}>
            <EditOutlined />
          </IconButton>
          <IconButton color="secondary" onClick={() => handleDeleteClick(row.id)}>
            <Delete style={{ color: "red" }} />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box m="0px">
      <DeleteDialog
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
        dutiesWeekId={selectedDutiesWeekId}
        onSuccess={loadDutiesWeeks}
      />

      <CreateDialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onSuccess={loadDutiesWeeks}
        selectedTermId={selectedTermId}
      />

      <EditDialog
        dutiesWeekId={selectedIdForEdit}
        open={isEditDialogOpen}
        onClose={() => {
          setIsEditDialogOpen(false);
          setSelectedIdForEdit(null);
        }}
        onSuccess={loadDutiesWeeks}
      />

      <Box p={2} ml={2} mr={2}>
        <Header title="DUTIES WEEKS" />
        
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 2, sm: 2 },
            mb: 2,
            alignItems: { xs: 'stretch', sm: 'center' },
            flexWrap: 'wrap'
          }}
        >
          <TextField
            select
            label="Select Term"
            value={selectedTermId && terms && terms.some(t => t.id === selectedTermId) ? selectedTermId : ''}
            onChange={handleTermChange}
            variant="filled"
            sx={{
              minWidth: { xs: '100%', sm: 250, md: 300 },
              maxWidth: { xs: '100%', sm: 300 }
            }}
            disabled={!terms || terms.length === 0}
          >
            {terms && terms.length > 0 ? (
              terms.map((term) => (
                <MenuItem key={term.id} value={term.id}>
                  {term.termName}
                </MenuItem>
              ))
            ) : (
              <MenuItem value="" disabled>
                No terms available
              </MenuItem>
            )}
          </TextField>

          {selectedTerm && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flex: { xs: '1 1 100%', sm: '0 1 auto' },
                minWidth: 0
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  color: colors.grey[100],
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  wordBreak: 'break-word',
                  textAlign: { xs: 'center', sm: 'left' }
                }}
              >
                Showing weeks for: <strong>{selectedTerm.termName}</strong>
              </Typography>
            </Box>
          )}

          <Button
            color="secondary"
            variant="contained"
            onClick={openCreateDialog}
            disabled={!selectedTermId}
            sx={{
              padding: { xs: "8px 16px", sm: "5px 8px" },
              fontSize: { xs: "14px", sm: "13px" },
              width: { xs: '100%', sm: 'auto' },
              marginLeft: { xs: 0, sm: 'auto' },
              minWidth: { xs: '100%', sm: 'auto' }
            }}
          >
            ADD DUTIES WEEK
          </Button>
        </Box>

        {!selectedTermId ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
            <Typography variant="h6" color="text.secondary">
              Please select a term to view duties weeks
            </Typography>
          </Box>
        ) : isLoading ? (
          <LoadingSpinner message="Loading duties weeks..." height="300px" />
        ) : (
          <Table
            rows={sortedDutiesWeeks}
            columns={columns}
            totalRecords={totalRecords}
            size={10}
            page={0}
            handlePageChange={() => {}}
            colors={colors}
          />
        )}
      </Box>
    </Box>
  );
};

export default DutiesWeek;

